import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { errorText } from "@/lib/api";
import { learn, isPdf } from "@/lib/learnApi";

// View-only study material. Pages arrive as watermarked images rendered on the server, so there is no file to save,
// no download or print button, and right-click / drag are blocked. (A screenshot is still possible; the watermark makes it traceable.)
const ViewerCtx = createContext(() => {});
export const useOpenMaterial = () => useContext(ViewerCtx);

const Page = ({ fid, i }) => {
  const [src, setSrc] = useState(null);
  const [near, setNear] = useState(i < 2);
  const ref = useRef(null);
  useEffect(() => {
    if (near || !ref.current) return undefined;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setNear(true), { rootMargin: "800px" });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [near]);
  useEffect(() => {
    if (!near) return undefined;
    let url;
    learn.viewPage(fid, i).then((u) => { url = u; setSrc(u); }).catch(() => setSrc("error"));
    return () => url && URL.revokeObjectURL(url);
  }, [fid, i, near]);
  if (!src) return <div ref={ref} className="grid aspect-[3/4] w-full place-items-center rounded-lg bg-white"><Loader2 className="h-6 w-6 animate-spin text-[#0E9384]" /></div>;
  if (src === "error") return <p className="rounded-lg bg-white p-6 text-center text-slate-600">Page {i + 1} could not load.</p>;
  return <img src={src} alt={`Page ${i + 1}`} draggable={false} className="w-full select-none rounded-lg bg-white shadow-sm" />;
};

export const MaterialViewerProvider = ({ children }) => {
  const [doc, setDoc] = useState(null);
  const open = useCallback(async (r) => {
    if (!isPdf(r)) {
      if (r.file_id) return learn.file(r.file_id).catch((e) => toast.error(errorText(e)));
      return window.open(r.url, "_blank", "noopener");
    }
    try { const info = await learn.viewInfo(r.file_id); setDoc({ fid: r.file_id, title: r.title, pages: info.pages }); }
    catch (e) { toast.error(errorText(e)); }
  }, []);
  useEffect(() => {
    if (!doc) return undefined;
    const block = (e) => { if ((e.metaKey || e.ctrlKey) && ["s", "p"].includes(e.key.toLowerCase())) e.preventDefault(); };
    window.addEventListener("keydown", block);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", block); document.body.style.overflow = ""; };
  }, [doc]);
  return (
    <ViewerCtx.Provider value={open}>
      {children}
      {doc && (
        <div data-testid="material-viewer" className="no-print fixed inset-0 z-[100] flex flex-col bg-[#0B1B33]/95" onContextMenu={(e) => e.preventDefault()}>
          <div className="flex items-center justify-between gap-3 px-5 py-3 text-white">
            <div className="min-w-0"><p className="truncate font-semibold">{doc.title}</p>
              <p className="flex items-center gap-1.5 text-[12px] text-slate-300"><ShieldCheck className="h-3.5 w-3.5" /> View-only · {doc.pages} pages · personalised to you</p></div>
            <button data-testid="material-viewer-close" onClick={() => setDoc(null)} className="rounded-lg p-2 hover:bg-white/10" aria-label="Close"><X className="h-6 w-6" /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-3 pb-10" data-lenis-prevent>
            <div className="mx-auto max-w-3xl space-y-4">{Array.from({ length: doc.pages }, (_, i) => <Page key={i} fid={doc.fid} i={i} />)}</div>
          </div>
        </div>
      )}
    </ViewerCtx.Provider>
  );
};
